import type { EntityType } from 'src/types/dto/audit-trail.dto';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import type { TypographyProps } from '@mui/material';
import { Typography } from '@mui/material';

import { paths } from 'src/routes/paths';

import { TaskDetailsModal } from 'src/sections/tasks/components/task-details-modal';
import { ActivityDetailsModal } from 'src/sections/visit/components/activity-details-modal';

interface Props {
  entityId: number | string;
  entityType: EntityType;
  label?: string | React.FC<{ onClick?: () => void }>;
  href?: string;
  variant?: TypographyProps['variant'];
  onClose?: () => void;
  disabled?: boolean;
  tabId?: 'details' | 'audit-trails';
}

const ENTITY_LINK_MAP: Record<
  EntityType,
  {
    link?: (id: number | string) => string;
    component?: React.FC<{
      id: number | string;
      open: boolean;
      onClose: () => void;
      tabId?: 'details' | 'audit-trails';
    }>;
    tabId?: 'details' | 'audit-trails';
  }
> = {
  Activity: {
    link: undefined,
    component: ActivityDetailsModal,
  },
  Project: {
    link: (id: number | string) => paths.dashboard.projects.auditTrails(id.toString()),
    component: undefined,
  },
  Task: {
    link: undefined,
    component: process.env.NEXT_PUBLIC_ENABLE_TASKS === 'true' ? TaskDetailsModal : undefined,
    tabId: 'details',
  },
  User: {
    link: (id: number | string) => paths.dashboard.organization.userEdit(id.toString()),
    component: undefined,
  },
  Client: {
    link: (id: number | string) => paths.dashboard.client.details(id.toString()),
    component: undefined,
  },
  Programme: {
    link: (id: number | string) => paths.dashboard.programmes.details(String(id)),
    component: undefined,
  },
};

export const EntityLinkManager = ({
  entityId,
  entityType,
  label,
  href,
  variant = 'body2',
  onClose,
  disabled = false,
  tabId: tabIdOverride,
}: Props) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const Dialog = ENTITY_LINK_MAP[entityType]?.component;
  const resolveHref = ENTITY_LINK_MAP[entityType]?.link;
  const tabId = tabIdOverride ?? ENTITY_LINK_MAP[entityType]?.tabId;
  const [open, setOpen] = useState(false);
  const handleClose = () => {
    setOpen(false);
    onClose?.();
  };
  const handleClick = () => {
    if (disabled) return;

    const destination = href ?? resolveHref?.(entityId);
    if (destination) {
      onClose?.();
      router.push(destination);
      return;
    }

    setOpen(true);
  };
  const LabelComponent = label;
  return (
    <>
      {!label || typeof label === 'string' ? (
        <Typography
          variant={variant}
          onClick={disabled ? undefined : handleClick}
          sx={{
            textDecoration: disabled ? 'none' : 'underline',
            cursor: disabled ? 'not-allowed' : 'pointer',
            opacity: disabled ? 0.6 : 1,
          }}
        >
          {label || `${t(`entityLink.${entityType}`)} #${entityId}`}
        </Typography>
      ) : (
        LabelComponent &&
        typeof LabelComponent === 'function' && (
          <LabelComponent onClick={disabled ? undefined : handleClick} />
        )
      )}
      {Dialog && open && (
        <Dialog id={entityId} open={open} onClose={handleClose} tabId={tabId ?? 'details'} />
      )}
    </>
  );
};
